import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Card, CardContent, CardHeader } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import {
  PROFILE_EDIT_SECTION_CARD_CONTENT_CLASS,
  PROFILE_EDIT_SECTION_CARD_HEADER_CLASS,
} from '@/core/components/profile/edit/profile-edit-surface';

type ProfileEditPageChromeProps = {
  title: string;
  description: string;
  breadcrumb?: ReactNode;
  onBack: () => void;
};

/** Body-only — parent route owns the stable `AppIonPage`. */
export function ProfileEditLoadingState({
  title,
  description,
  breadcrumb,
  onBack,
}: ProfileEditPageChromeProps) {
  return (
    <AppPageContent hideScrollbar>
      <AppPageLayout className="overflow-x-hidden min-w-0 max-w-full md:pb-4">
        <PageHeader
          title={title}
          description={description}
          breadcrumb={breadcrumb}
          onBack={onBack}
        />
        <div className="flex min-w-0 flex-col gap-4 md:gap-5">
          <Card className="min-w-0">
            <CardHeader className={PROFILE_EDIT_SECTION_CARD_HEADER_CLASS}>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="mt-2 h-4 w-64" />
            </CardHeader>
            <CardContent
              className={`space-y-4 ${PROFILE_EDIT_SECTION_CARD_CONTENT_CLASS}`}
            >
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-24 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </AppPageLayout>
    </AppPageContent>
  );
}

type ProfileEditErrorStateProps = ProfileEditPageChromeProps & {
  errorMessage: string;
  retryLabel: string;
  onRetry: () => void;
};

/** Body-only — parent route owns the stable `AppIonPage`. */
export function ProfileEditErrorState({
  title,
  description,
  breadcrumb,
  onBack,
  errorMessage,
  retryLabel,
  onRetry,
}: ProfileEditErrorStateProps) {
  return (
    <AppPageContent>
      <AppPageLayout className="overflow-x-hidden min-w-0 max-w-full md:pb-4">
        <PageHeader
          title={title}
          description={description}
          breadcrumb={breadcrumb}
          onBack={onBack}
        />
        <div className="flex min-w-0 flex-col gap-4 md:gap-5">
          <Card className="min-w-0">
            <CardContent
              className={`flex flex-col gap-4 ${PROFILE_EDIT_SECTION_CARD_CONTENT_CLASS}`}
            >
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
              <Button variant="primary" onClick={onRetry}>
                {retryLabel}
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppPageLayout>
    </AppPageContent>
  );
}
